import React from "react";
import { fmtDate } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2, Paperclip } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { money, FEE_STATUS_META, FEE_PAID_BY_META, feeTotal, invoicedToLabel, reimbursementBadge } from "./feeUtils";

export const FeeRow = ({ f, project = {}, onQuickStatus, onEdit, onRemove, onPreview }) => {
  const paidBy = FEE_PAID_BY_META[f.paid_by];
  const invTo = invoicedToLabel(f, project);
  const reimb = reimbursementBadge(f);
  const atts = f.attachments || [];
  return (
    <div className="px-4 py-3 border-b border-zinc-100 last:border-0 grid grid-cols-[minmax(0,1fr)_120px_130px_110px_70px] gap-3 items-center group" data-testid={`fee-row-${f.fee_id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{f.label}</p>
        {f.invoice_number && <p className="text-[11px] text-zinc-400 truncate">Inv #{f.invoice_number}</p>}
        {f.status === "paid" && f.paid_date && <p className="text-[11px] text-emerald-600 truncate">Paid {fmtDate(f.paid_date)}</p>}
        {(paidBy || invTo) && (
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {paidBy && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border ${paidBy.cls}`}>{paidBy.label}</span>}
            {invTo && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border bg-zinc-100 text-zinc-600 border-zinc-200 truncate max-w-[160px]">Bill → {invTo}</span>}
            {reimb && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border ${reimb.cls}`}>{reimb.label}{reimb.label === "Reimbursed" && f.reimbursed_date ? ` ${fmtDate(f.reimbursed_date)}` : ""}</span>}
          </div>
        )}
        {atts.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {atts.map((a) => (
              <button key={a.file_id} onClick={() => onPreview?.(a)} title={a.original_filename}
                className="inline-flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-900 border border-zinc-200 rounded-sm px-1.5 py-0.5 max-w-[150px]"
                data-testid={`fee-row-attachment-${a.file_id}`}>
                <Paperclip className="h-3 w-3 shrink-0" />
                <span className="truncate">{a.label || a.original_filename}</span>
              </button>
            ))}
          </div>
        )}
        {f.note && <p className="text-xs text-zinc-400 truncate mt-0.5">{f.note}</p>}
      </div>
      <span className="text-sm tabular-nums">
        <span className="font-semibold">{money.format(feeTotal(f))}</span>
        {f.gst && <span className="block text-[10px] text-zinc-400">incl. GST</span>}
      </span>
      <Select value={f.status} onValueChange={(v) => onQuickStatus(f, v)}>
        <SelectTrigger className={`h-8 rounded-full border text-xs font-semibold w-[120px] ${FEE_STATUS_META[f.status]?.cls || ""}`} data-testid={`fee-status-${f.fee_id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.keys(FEE_STATUS_META).map((s) => <SelectItem key={s} value={s}>{FEE_STATUS_META[s].label}</SelectItem>)}
        </SelectContent>
      </Select>
      <span className="text-xs text-zinc-600">{f.due_date ? fmtDate(f.due_date) : "—"}</span>
      <span className="flex justify-end gap-1">
        <button onClick={() => onEdit(f)} className="text-zinc-400 hover:text-zinc-900 p-1" data-testid={`fee-edit-${f.fee_id}`}><Pencil className="h-3.5 w-3.5" /></button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="text-zinc-400 hover:text-red-600 p-1" data-testid={`fee-delete-${f.fee_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-sm">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove this fee?</AlertDialogTitle>
              <AlertDialogDescription>&ldquo;{f.label}&rdquo; ({money.format(f.amount || 0)}) will be permanently removed.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={() => onRemove(f.fee_id)}>Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </span>
    </div>
  );
};
